import React from 'react'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { Grid, Button, Stack, Typography, IconButton, Link as MuiLink, styled, css } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NavigateNext from '@mui/icons-material/NavigateNext'
import loaderLight from '@assets/images/loader-light.svg'
import { getProductPriceWithSelectionsV2 } from '@helpers/cart'
import { currencyFormatUS, slugify } from '@helpers/string-helper'
import { getProductSaleFlagType, earliestStockDate } from '@helpers/product'
import { getCurrentLocation } from '@helpers/geo-location'
import { productUrl } from '@helpers/route'
import { ButtonLink } from '@templates/strapi-cms/content-types/Link'
import useCartV2 from '@hooks/useCartV2'
import PriceAtom from '../../@rtg2022/components/atoms/Price/Price'
import CouponTag from '../../@rtg2022/components/atoms/CouponTag/CouponTag'

export const getProperlySizedImageUrl = url => {
  if (url) {
    const searchParam = 'h=50'
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${searchParam}`
  }
  return ''
}

const GridWrapper = styled(Grid)(
  ({ theme }) => css`
    .strikethrough {
      font-size: 0.75rem;
      line-height: 0.75rem;
    }

    .strikethrough-sale,
    .highlight-price {
      color: ${theme.palette.error.main};
    }
  `,
)

const StyledPromoLabel = styled('span', {
  shouldForwardProp: props => props !== 'isBonusBuy',
})(({ theme, isBonusBuy }) => ({
  padding: '4px',
  backgroundColor: isBonusBuy ? theme.palette.error.main : theme.palette.success.main,
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '.5rem',
  borderRadius: '4px',
  color: theme.palette.common.white,
  position: 'absolute',
  alignSelf: 'flex-start',
  top: '-5px',
  left: '-8px',
}))

const LoadingSpinner = styled('img')({
  height: '1.25rem',
  width: '1.25rem',
  margin: '0',
})

function getInternalUrl(isGiftCard, giftCardRoute, route, sku, product) {
  if (isGiftCard) {
    return giftCardRoute
  }
  if (route) {
    return productUrl(route)
  }
  if (product?.isContainerSku) {
    const slotsObject = {}
    product.slotFillers?.forEach(slotFiller => {
      slotsObject[slotFiller.slotName] = slotFiller.sku
    })
    const slots = JSON.stringify(slotsObject)
    return `/furniture/product/${slugify(product?.title ?? '')}/${sku}?slots=${slots}`
  }
  return null
}

function isLinkDisabled(product, route) {
  if (product?.category === 'gift-card') {
    return true
  }
  if (!route) {
    if (product?.isContainerSku) {
      return false
    }
    return true
  }
  return false
}

const MiniCartV2 = () => {
  const { order } = useSelector(state => state.checkout)
  const { region, distribution_index } = getCurrentLocation()
  const { cart: cartV2, loading, removeCartLineItemMutation } = useCartV2()

  const normalizeCartItems = cart => {
    if (!cart) return []
    if (cart?.lineItems?.length > 0) {
      return cart.lineItems.map(item => ({
        uid: item.lineItemId,
        sku: item.sku,
        quantity: item.quantity,
        product: {
          isContainerSku: item?.isContainerSku,
          slotFillers: item?.slotFillers,
          isConfigurable: item?.isConfigurable || item?.isStoreSku, // Grouping in store cart here since this is just used to avoid assigning link
          isStrikethrough:
            (item?.productDetails?.isStrikethrough ?? false) ||
            (item?.thankYouCouponApplied && item?.strikePrice !== item?.unitPrice),
          on_sale: item?.productDetails?.onSale,
          price: item.unitPrice,
          primary_image: item?.productDetails?.imageUrl || item?.imageUrl,
          route: item?.productDetails?.route || null,
          saleFlag: item?.productDetails?.saleFlag,
          strikePrice: item?.strikePrice,
          title: item?.productDetails?.title || item?.title,
        },
        containerSavings: item?.productDetails?.containerSavings || null,
        promoInfo: item?.promotionStatus ? { bonusItemSavings: item?.bonusItemSavings ?? 0 } : null,
        promotion: null,
        promotionStatus: item?.promotionStatus,
        price: item.unitPrice,
      }))
    }
    return []
  }
  const normalizedCartItems = normalizeCartItems(cartV2)

  const handleRemoveFromCart = async uid => {
    await removeCartLineItemMutation.mutateAsync(uid)
  }

  const cartTotal = cartV2?.cartTotal ?? 0
  const checkoutUrl = cartV2?.isCartAvailable ? '/checkout' : '/cart'

  return (
    <Grid container spacing={1}>
      <Grid item sm={12}>
        <Typography id="cartSubtotal" color="error" variant="subtitle1" textAlign="center">
          Subtotal:{' '}
          <b>
            <PriceAtom price={currencyFormatUS(cartTotal)} />
          </b>
        </Typography>
      </Grid>

      <Grid item sm={12}>
        <Button
          component={ButtonLink}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          data={{
            InternalUrl: checkoutUrl,
            Title: 'Go to Checkout',
          }}
        >
          Checkout
        </Button>
      </Grid>
      <Grid item sm={12}>
        <Button
          size="small"
          component={ButtonLink}
          data={{
            InternalUrl: '/cart',
            Title: 'View Full Cart',
            DisplayText: 'View Full Cart',
          }}
          fullWidth
          trackingData={{ category: 'header', action: 'cart interaction', label: 'view full cart' }}
          endIcon={<NavigateNext />}
        >
          View Full Cart
        </Button>
      </Grid>
      {(!cartV2 || (normalizedCartItems && normalizedCartItems.length === 0)) && (
        <Grid item sm={12}>
          <Typography textAlign="center" variant="body2">
            Your shopping cart is empty
          </Typography>
        </Grid>
      )}
      {loading && (
        <>
          <LoadingSpinner alt="Assigning BitPay as payment type" src={loaderLight} />
        </>
      )}
      {!loading &&
        normalizedCartItems &&
        normalizedCartItems.length > 0 &&
        normalizedCartItems.map((item, index) => {
          const { product, quantity, sku, uid, price, promoInfo, containerSavings } = item
          const { isConfigurable: isConfiguratorItem = false } = product
          let route = null
          if (product && !isConfiguratorItem && product?.route) route = product?.route

          const title = item.containerSku ? item.containerName : product?.displayNamePDP ?? product?.title
          const productImage =
            item?.containerSku && item?.containerImage ? item?.containerImage : product?.primary_image || product?.image

          const strikethrough = product.price < product.strikePrice
          const strikeThroughPrice = strikethrough ? product?.strikePrice : null

          const additionalOptionLineItem = order?.lineItems?.find(
            lineItem => lineItem?.sku === sku && lineItem?.additionalOption,
          )
          const additionalOptionSavings =
            additionalOptionLineItem?.additionalOptionPrice?.savings || item?.additionalOptionPrice?.savings || 0
          const savings = promoInfo?.bonusItemSavings || additionalOptionSavings || 0
          const fullPrice = getProductPriceWithSelectionsV2(
            cartV2?.lineItems?.find(lineItem => lineItem.lineItemId === uid) ?? {},
            savings,
          )

          const isGiftCard = sku === '83333333'
          const giftCardRoute = 'gift-cards/purchase'
          const isBonusBuy = item?.product?.saleFlag?.includes('BONUS')
          const isMainPromoItem = item?.promotionStatus === 'UTILIZED'
          const isTargetPromoItem = item?.promotionStatus === 'APPLIED'

          return (
            <Grid container item sm={12} key={uid || sku} spacing={1}>
              <hr />
              <Grid container item sm={11} spacing={1}>
                <Grid item sm={4}>
                  {isConfiguratorItem ? (
                    <Stack justifyContent="center">
                      <img
                        style={{
                          height: '80px',
                          width: 'auto',
                          margin: '0 auto',
                        }}
                        src={getProperlySizedImageUrl(productImage)}
                        alt={title}
                        role="presentation"
                      />
                    </Stack>
                  ) : (
                    <MuiLink
                      component={ButtonLink}
                      data={{
                        InternalUrl: getInternalUrl(isGiftCard, giftCardRoute, route, sku, product),
                      }}
                      aria-hidden="true"
                      sx={t => ({
                        display: 'flex',
                        justifyContent: 'center',
                        border: 'solid 2px transparent',
                        '&:focus': {
                          border: `solid 2px ${t.palette.primary.main}`,
                        },
                      })}
                      disabled={isLinkDisabled(product, route)}
                    >
                      <img
                        style={{
                          height: '60px',
                          width: 'auto',
                        }}
                        src={getProperlySizedImageUrl(productImage)}
                        alt={product?.title}
                        role="presentation"
                      />
                      {isTargetPromoItem && <StyledPromoLabel isBonusBuy={isBonusBuy}>OFFER INCLUDED</StyledPromoLabel>}
                    </MuiLink>
                  )}
                </Grid>

                <Grid container item sm={8}>
                  {isConfiguratorItem ? (
                    <Grid item sm={12}>
                      <p style={{ fontSize: '0.9rem' }}>{product?.title}</p>
                    </Grid>
                  ) : (
                    <Grid item sm={12}>
                      {isMainPromoItem && <CouponTag isBonusBuy={isBonusBuy} />}
                      <MuiLink
                        component={ButtonLink}
                        data={{
                          InternalUrl: getInternalUrl(isGiftCard, giftCardRoute, route, sku, product),
                        }}
                        sx={{
                          fontSize: '0.9rem',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          '&:focus': {
                            textDecoration: 'underline',
                          },
                          ...(isLinkDisabled(product, route) && {
                            '&:hover': {
                              textDecoration: 'none',
                            },
                          }),
                        }}
                        aria-describedby={`mcq_${index}mcp_${index}`}
                        disabled={isLinkDisabled(product, route)}
                      >
                        <span style={{ textTransform: 'capitalize' }} dangerouslySetInnerHTML={{ __html: title }} />
                      </MuiLink>
                    </Grid>
                  )}

                  <Grid item sm={6}>
                    <Typography sx={{ fontSize: '0.8rem' }} variant="body2">
                      Qty: {quantity}
                    </Typography>
                  </Grid>

                  <GridWrapper item sm={6}>
                    {strikethrough && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.8rem', textAlign: 'right' }}
                        className="strikethrough"
                      >
                        <PriceAtom strikeThrough price={strikeThroughPrice} />
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.8rem', textAlign: 'right' }}
                      className={classNames(
                        strikethrough ? 'strikethrough-sale' : '',
                        product?.on_sale || !!promoInfo?.bonusItemSavings ? 'highlight-price' : '',
                      )}
                    >
                      <PriceAtom price={currencyFormatUS(product?.price ? fullPrice : price)} />
                    </Typography>
                  </GridWrapper>

                  {containerSavings && (
                    <>
                      <Grid item sm={6} />
                      <GridWrapper item sm={6}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.625rem', textAlign: 'right' }}
                          className={classNames('highlight-price')}
                        >
                          YOU SAVE <PriceAtom price={currencyFormatUS(containerSavings)} />
                        </Typography>
                      </GridWrapper>
                    </>
                  )}
                </Grid>
              </Grid>

              <Grid item sm={1}>
                <IconButton title="Remove from cart" size="small" onClick={() => handleRemoveFromCart(uid)}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          )
        })}
    </Grid>
  )
}

export default MiniCartV2
